import { useMemo } from 'react';
import { Box, Button, Tooltip, Typography } from '@mui/material';
import LightbulbOutlinedIcon from '@mui/icons-material/LightbulbOutlined';
import type {
  GridColDef,
  GridRenderCellParams,
} from '@mui/x-data-grid-premium';
import ActionsMenuCell from '@/components/common/ActionsMenuCell';
import type { AdvisorInvestor } from '@/store/usersApi';
import { formatNumberToCurrency } from '@/utils/currency';
import { convertNumberToPercentage } from '@/utils/numbers';
import { formatAppDate, parseAppDate } from '@/utils/date';
import InsightsCell from '../components/InsightsCell';
import { actionColumnConfig } from '@/components/grids/gridColumnConfigs';
import { useIntegration } from '@/permissions/hooks/useIntegration';

// Matches the app's useLocalization() t (and the sibling useClientListViewColumn
// hook). The columns only ever call t(key, defaultValue), so this narrow shape is
// all that's needed — and unlike i18next's branded TFunction, LocalizationT satisfies it.
export type TranslateFn = (key: string, defaultValue?: string) => string;

/**
 * Display helpers shared by the desktop grid columns and the XS card (list)
 * view so both render a client's fields identically. Keep these pure.
 */
/**
 * Sort value for the date columns. Their cells show a formatted date, and the
 * advisor investors endpoint may send a Microsoft JSON date, which `new Date`
 * cannot read. Ordering therefore runs on a parsed timestamp.
 */
export const clientDateSortValue = (
  value: string | null | undefined,
): number | null => parseAppDate(value)?.valueOf() ?? null;

export const formatInvestorNetWorth = (row: AdvisorInvestor): string => {
  if (row.IsSensitiveUser) return 'N/A';
  return typeof row.NetWorth === 'number'
    ? formatNumberToCurrency(row.NetWorth)
    : '-';
};

export const formatInvestorHomePercentage = (row: AdvisorInvestor): string => {
  const hasApiValue = typeof row.HomePercentage === 'number';
  const computed =
    !hasApiValue && typeof row.TotalAssets === 'number' && row.TotalAssets > 0
      ? (row.Home / row.TotalAssets) * 100
      : undefined;
  const raw = hasApiValue ? row.HomePercentage : computed;
  if (typeof raw !== 'number') return '-';
  const fraction = raw > 1 ? raw / 100 : raw;
  return convertNumberToPercentage(fraction);
};

export const formatInvestorLastLogin = (
  row: AdvisorInvestor,
  t: TranslateFn,
): string => {
  if (row.OnboardingStep === 1 && !row.InHousehold) {
    return t('inviteStatus.notInvited', 'Not invited');
  }
  if (row.OnboardingStep === 2 && !row.InHousehold) {
    const invitedOn = formatAppDate(row.LastInviteDate, { fallback: '' });
    if (!invitedOn) return t('inviteStatus.invited', 'Invited');
    return t('clientGrid.invitedOn', 'Invited on {{date}}').replace(
      '{{date}}',
      invitedOn,
    );
  }
  return formatAppDate(row.LastLogin, { fallback: '-' });
};

export const useClientColumns = (args: {
  gridColumns:
    | {
        Records?: Array<{
          IsEnabled: boolean;
          Label: string;
          Order: number;
          GridColumnDataField: string;
        }>;
      }
    | undefined;
  anyFullControl: boolean;
  t: TranslateFn;
  selectedGroupingId: number | undefined;
  selectedGroupingLabel: string;
  groupBy: string;
  MenuForInvestor: React.ComponentType<{
    investor: AdvisorInvestor;
    hideTrigger?: boolean;
    size?: 'small' | 'medium' | 'large';
  }>;
  openMenuForInvestor: (
    investor: AdvisorInvestor,
    e: React.MouseEvent<HTMLElement>,
  ) => void;
  shouldFetchInsights: boolean;
  onNameClick: (investor: AdvisorInvestor) => void;
  onAccessInvestor?: (investor: AdvisorInvestor) => void;
  isAccessingInvestor?: boolean;
  accessingInvestorId?: number | null;
}) => {
  const {
    gridColumns,
    anyFullControl,
    t,
    selectedGroupingId,
    selectedGroupingLabel,
    groupBy,
    MenuForInvestor,
    openMenuForInvestor,
    shouldFetchInsights,
    onNameClick,
    onAccessInvestor,
    isAccessingInvestor,
    accessingInvestorId,
  } = args;
  const { isIntegrationEnabled } = useIntegration();
  const isOrionEnabled = isIntegrationEnabled('Orion');

  const columns: GridColDef<AdvisorInvestor>[] = useMemo(() => {
    const enabled = (gridColumns?.Records ?? [])
      .filter((c) => c.IsEnabled)
      .filter((c) =>
        typeof c.Label === 'string'
          ? c.Label.toLowerCase() !== 'details'
          : true,
      )
      .sort((a, b) => a.Order - b.Order);

    const mapped: GridColDef<AdvisorInvestor>[] = enabled.flatMap((c) => {
      const field = c.GridColumnDataField;

      const base: GridColDef<AdvisorInvestor> = {
        field,
        headerName: c.Label,
        flex: 1,
        sortable: true,
      };

      switch (c.GridColumnDataField) {
        case 'Name': {
          return {
            ...base,
            renderCell: (params: GridRenderCellParams<AdvisorInvestor>) => (
              <Box
                component='span'
                sx={{ cursor: 'pointer' }}
                title={String(params.value ?? '')}
                onClick={() => {
                  onNameClick(params.row);
                }}
              >
                {String(params.value ?? '')}
              </Box>
            ),
          };
        }
        case 'UserName': {
          return {
            ...base,
            renderCell: (params: GridRenderCellParams<AdvisorInvestor>) => (
              <span title={String(params.value ?? '')}>
                {String(params.value ?? '')}
              </span>
            ),
          };
        }
        case 'NetWorth': {
          const deriveNetWorthValue = (row: AdvisorInvestor): number | null => {
            if (row.IsSensitiveUser) return null;
            return typeof row.NetWorth === 'number' ? row.NetWorth : null;
          };

          return {
            ...base,
            type: 'number',
            filterable: true,
            valueGetter: (_value: number | null, row: AdvisorInvestor) =>
              deriveNetWorthValue(row),
            renderCell: (params: GridRenderCellParams<AdvisorInvestor>) =>
              formatInvestorNetWorth(params.row),
          } as unknown as GridColDef<AdvisorInvestor>;
        }
        case 'Home': {
          return {
            ...base,
            type: 'number',
            sortable: true,
            filterable: true,
            valueFormatter: (value: number | null | undefined) =>
              formatNumberToCurrency(typeof value === 'number' ? value : 0),
          } as unknown as GridColDef<AdvisorInvestor>;
        }
        case 'HomePercentage': {
          return {
            ...base,
            type: 'number',
            sortable: true,
            filterable: true,
            renderCell: (params: GridRenderCellParams<AdvisorInvestor>) =>
              formatInvestorHomePercentage(params.row),
            valueGetter: (_value: number | null, row: AdvisorInvestor) => {
              const hasApiValue = typeof row.HomePercentage === 'number';
              const computed =
                !hasApiValue &&
                typeof row.TotalAssets === 'number' &&
                row.TotalAssets > 0
                  ? (row.Home / row.TotalAssets) * 100
                  : undefined;
              const raw = hasApiValue ? row.HomePercentage : computed;
              if (typeof raw !== 'number') return null;
              const fraction = raw > 1 ? raw / 100 : raw;
              return fraction;
            },
          } as unknown as GridColDef<AdvisorInvestor>;
        }
        case 'LastLogin': {
          return {
            ...base,
            type: 'number',
            valueGetter: (_value: string | null, row: AdvisorInvestor) =>
              clientDateSortValue(row.LastLogin),
            valueFormatter: (_value: number | null, row: AdvisorInvestor) =>
              formatInvestorLastLogin(row, t),
          } as unknown as GridColDef<AdvisorInvestor>;
        }
        case 'Insights': {
          return {
            ...base,
            sortable: true,
            filterable: false,
            renderHeader: () => (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Typography
                  component='span'
                  sx={{ fontSize: 14, fontWeight: 600 }}
                >
                  {t('insights', 'Insights')}
                </Typography>
                <LightbulbOutlinedIcon
                  sx={{ fontSize: 18, color: 'text.secondary' }}
                />
              </Box>
            ),
            renderCell: (params: GridRenderCellParams<AdvisorInvestor>) => (
              <InsightsCell
                investorId={params.row.InvestorId}
                shouldFetch={shouldFetchInsights}
              />
            ),
          };
        }
        case 'OnboardingStepDisplay': {
          return {
            ...base,
            headerName: 'Status',
          };
        }
        case 'LastOrionLogin': {
          if (!isOrionEnabled) {
            return [];
          }

          // Numeric like LastLogin: the getter yields an epoch, so the filter
          // menu offers number operators and the cell aligns with the other
          // numeric columns.
          return {
            ...base,
            type: 'number',
            valueGetter: (_value: string | null, row: AdvisorInvestor) =>
              clientDateSortValue(row.LastOrionLogin),
            valueFormatter: (_value: number | null, row: AdvisorInvestor) =>
              formatAppDate(row.LastOrionLogin, { fallback: '-' }),
          } as unknown as GridColDef<AdvisorInvestor>;
        }
        default:
          return base;
      }
    });

    // Grouping columns aren't backed by a sortable model property; server sort
    // would silently no-op, so don't offer the affordance.
    const dynamicGroupingCol: GridColDef<AdvisorInvestor> | null =
      selectedGroupingId
        ? {
            field: `group_${String(selectedGroupingId)}`,
            headerName: selectedGroupingLabel,
            sortable: false,
          }
        : null;

    const onboardingGroupingCol: GridColDef<AdvisorInvestor> | null =
      groupBy === 'OnboardingStepDisplay'
        ? {
            field: 'OnboardingStepDisplay',
            headerName: selectedGroupingLabel,
            sortable: false,
          }
        : null;

    const actionsCol: GridColDef<AdvisorInvestor> = {
      field: 'actions',
      width: 56,
      ...actionColumnConfig,
      renderCell: (params: GridRenderCellParams<AdvisorInvestor>) => {
        type ParamsWithRowNode = GridRenderCellParams<AdvisorInvestor> & {
          rowNode?: { type?: string };
        };
        const node = (params as ParamsWithRowNode).rowNode;
        if (node.type === 'group') return null;
        const investor = params.row;
        const TriggerlessMenu = MenuForInvestor;
        return (
          <Box
            sx={{
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '100%',
            }}
          >
            <ActionsMenuCell
              size='small'
              onClick={(e) => {
                openMenuForInvestor(investor, e);
              }}
            />
            <TriggerlessMenu
              investor={investor}
              hideTrigger
              size='small'
            />
          </Box>
        );
      },
    };

    const accessCol: GridColDef<AdvisorInvestor> = {
      field: 'access',
      headerName: t('impersonate', 'Impersonate'),
      renderHeader: () => (
        <Tooltip title={t('impersonate', 'Impersonate')}>
          <span>{t('impersonate', 'Impersonate')}</span>
        </Tooltip>
      ),
      sortable: false,
      filterable: false,
      hideable: false,
      disableColumnMenu: true,
      pinnable: false,
      width: !anyFullControl ? 90 : 120,
      renderCell: (params: GridRenderCellParams<AdvisorInvestor>) => {
        const canAccess = params.row.CanImpersonate;
        if (!canAccess) return null;
        const investor = params.row;
        const isActive =
          Boolean(isAccessingInvestor) &&
          accessingInvestorId !== undefined &&
          accessingInvestorId !== null &&
          accessingInvestorId === investor.InvestorId;
        return (
          <Box
            sx={{
              height: '100%',
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Button
              size='small'
              disabled={isActive}
              onClick={(e) => {
                e.stopPropagation();
                onAccessInvestor?.(investor);
              }}
            >
              {t('access', 'Access')}
            </Button>
          </Box>
        );
      },
    };

    // NOTE: the Insights column is intentionally NOT injected here.
    // It has no backend model — there is no `Insights` row in the
    // ADVISOR_CLIENTS `GridColumns` config and no gating `ModuleFeature` — so a
    // force-injected column is always visible and cannot be disabled per firm,
    // which violates FR #4. It has therefore been removed pending a backend
    // change. The `case 'Insights'` renderer above is retained: once the backend
    // adds an `Insights` GridColumns row, the column will render from
    // `gridColumns.Records` and honor firm enablement automatically.
    // See docs/advisor/advisor-grid-conditional-columns.md.

    return [
      ...(dynamicGroupingCol ? [dynamicGroupingCol] : []),
      ...(onboardingGroupingCol ? [onboardingGroupingCol] : []),
      ...mapped,
      accessCol,
      actionsCol,
    ];
  }, [
    gridColumns?.Records,
    selectedGroupingId,
    selectedGroupingLabel,
    groupBy,
    t,
    anyFullControl,
    onNameClick,
    shouldFetchInsights,
    isOrionEnabled,
    MenuForInvestor,
    openMenuForInvestor,
    isAccessingInvestor,
    accessingInvestorId,
    onAccessInvestor,
  ]);

  return columns;
};
